import { useState } from 'react'
import { FileText, Plus, Edit, Trash2, Eye, EyeOff, Briefcase, GraduationCap, BookOpen } from 'lucide-react'
import toast from 'react-hot-toast'
import { talents } from '../../services/apiService'
import { getCVTypeLabel, getCVTypeColor, getCVTypeOptions, getCVTypePlaceholders, formatCVDateRange } from '../../utils/cvTypeUtils'
import { getProfessionName, getDepartmentName } from '../../utils/professionUtils'
import { TextField, DatePicker, Checkbox, Autocomplete } from '@anyemp/ui-kit'
import SectionBlock from '../UI/SectionBlock'
import SectionHeader from '../UI/SectionHeader'
import ConfirmDialog from '../UI/ConfirmDialog'

const cvTypeOptions = getCVTypeOptions()

const CVTypeIcon = ({ type, className }) => {
  const icons = { experiences: Briefcase, education: GraduationCap, courses: BookOpen }
  const Icon = icons[type] || Briefcase
  return <Icon className={className} />
}

const CVForm = ({ formData, setFormData, onSubmit, onCancel, isNew }) => {
  const placeholders = getCVTypePlaceholders(formData.cv_type || 'experiences')
  return (
    <div className="border border-light-border-primary dark:border-dark-border-primary rounded-lg p-4 bg-light-bg-tertiary dark:bg-dark-bg-tertiary">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Autocomplete
          fullWidth
          label="Record Type *"
          value={formData.cv_type}
          onChange={(value) => setFormData(p => ({ ...p, cv_type: value || '' }))}
          placeholder="Select type"
          options={cvTypeOptions}
          required
        />
        <div>
          <TextField
            label="Company / Institution"
            required
            fullWidth
            type="text"
            value={formData.company_name}
            onChange={(e) => setFormData(p => ({ ...p, company_name: e.target.value }))}
            placeholder={placeholders.company_name}
          />
        </div>
        <div>
          <TextField
            label="Position / Specialisation"
            fullWidth
            type="text"
            value={formData.specialisation}
            onChange={(e) => setFormData(p => ({ ...p, specialisation: e.target.value }))}
            placeholder={placeholders.specialisation}
          />
        </div>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          <div>
            <DatePicker
              label="Start Date"
              fullWidth
              value={formData.start_date ? new Date(formData.start_date) : null}
              onChange={(d) => setFormData(p => ({ ...p, start_date: d ? d.toISOString().slice(0, 10) : '' }))}
            />
          </div>
          <div>
            <DatePicker
              label="End Date"
              fullWidth
              value={formData.end_date ? new Date(formData.end_date) : null}
              onChange={(d) => setFormData(p => ({ ...p, end_date: d ? d.toISOString().slice(0, 10) : '' }))}
            />
          </div>
        </div>
      </div>
      <div className="mt-3">
        <TextField
          label="Description"
          multiline
          rows={3}
          fullWidth
          value={formData.description}
          onChange={(e) => setFormData(p => ({ ...p, description: e.target.value }))}
          placeholder="Describe responsibilities and achievements..."
        />
      </div>
      <div className="mt-3">
        <Checkbox
          label="Visible on public profile"
          color="success"
          size="small"
          checked={!!formData.public_visible}
          onChange={(checked) => setFormData(p => ({ ...p, public_visible: checked }))}
        />
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        <button
          type="button"
          onClick={onSubmit}
          className="px-3 py-1.5 bg-primary-600 hover:bg-primary-700 dark:bg-primary-500 dark:hover:bg-primary-600 text-white text-sm font-medium rounded-lg transition-colors"
        >
          {isNew ? 'Add CV' : 'Save'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="px-3 py-1.5 border border-light-border-primary dark:border-dark-border-primary text-sm text-light-text-secondary dark:text-dark-text-secondary rounded-lg hover:bg-light-bg-secondary dark:hover:bg-dark-bg-secondary transition-colors"
        >
          Cancel
        </button>
      </div>
    </div>
  )
}

const emptyForm = { cv_type: 'experiences', company_name: '', specialisation: '', start_date: '', end_date: '', description: '', public_visible: true }

const CVsSection = ({ talent, talentId, hasPermission, setTalent }) => {
  const talentType = talent?.talent_type

  const canCreate = hasPermission('talent-service', `${talentType}_cv`, 'create')
  const canUpdate = hasPermission('talent-service', `${talentType}_cv`, 'update')
  const canDelete = hasPermission('talent-service', `${talentType}_cv`, 'delete')

  // null = closed | { id: 'new' } = add form at top | { id: X, ... } = edit inline
  const [editingCV, setEditingCV] = useState(null)
  const [formData, setFormData] = useState(emptyForm)
  const [deleteConfirm, setDeleteConfirm] = useState(null) // cv object to delete

  const handleCreate = () => {
    setEditingCV({ id: 'new' })
    setFormData(emptyForm)
  }

  const handleEdit = (cv) => {
    setEditingCV(cv)
    setFormData({
      cv_type: cv.cv_type || 'experiences',
      company_name: cv.company_name || '',
      specialisation: cv.specialisation || '',
      start_date: cv.start_date || '',
      end_date: cv.end_date || '',
      description: cv.description || '',
      public_visible: cv.public_visible !== false
    })
  }

  const handleCancel = () => {
    setEditingCV(null)
    setFormData(emptyForm)
  }

  const handleSubmit = async () => {
    if (!formData.company_name.trim() || !formData.cv_type) {
      toast.error('Company name and record type are required')
      return
    }

    try {
      if (editingCV?.id === 'new') {
        const result = await talents.addTalentCV(talentId, formData)
        if (result.success) {
          setTalent(prev => ({ ...prev, cv: [...(prev.cv || []), result.data] }))
        }
      } else {
        const result = await talents.updateTalentCV(talentId, editingCV.id, formData)
        if (result.success) {
          setTalent(prev => ({
            ...prev,
            cv: prev.cv.map(c => c.id === editingCV.id ? result.data : c)
          }))
        }
      }
      setEditingCV(null)
      setFormData(emptyForm)
    } catch (error) {
      console.error('Error saving CV:', error)
      toast.error('Error saving CV: ' + (error.response?.data?.message || error.message))
    }
  }

  const handleDelete = (cv) => {
    setDeleteConfirm(cv)
  }

  const confirmDelete = async () => {
    try {
      await talents.deleteTalentCV(talentId, deleteConfirm.id)
      setTalent(prev => ({ ...prev, cv: prev.cv.filter(c => c.id !== deleteConfirm.id) }))
    } catch (error) {
      console.error('Error deleting CV:', error)
      toast.error('Error deleting CV: ' + (error.response?.data?.message || error.message))
    }
  }

  const handleToggleVisibility = async (cvId, currentVisibility) => {
    try {
      const result = await talents.updateTalentCV(talentId, cvId, { public_visible: !currentVisibility })
      if (result.success) {
        setTalent(prev => ({
          ...prev,
          cv: prev.cv.map(c => c.id === cvId ? { ...c, public_visible: !currentVisibility } : c)
        }))
      }
    } catch (error) {
      console.error('Error toggling CV visibility:', error)
      toast.error('Error updating visibility: ' + (error.response?.data?.message || error.message))
    }
  }

  const cvList = talent?.cv || []

  return (
    <>
    <SectionBlock id="cvs">
      <SectionHeader
        icon={FileText}
        title={cvList.length > 0 ? `CVs (${cvList.length})` : 'CVs'}
        action={canCreate && (
          <button
            onClick={handleCreate}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-primary-600 text-white text-xs font-medium rounded-lg hover:bg-primary-700 dark:bg-primary-500 dark:hover:bg-primary-600 transition-colors"
          >
            <Plus className="h-3.5 w-3.5" />Add CV
          </button>
        )}
      />

      {/* Add form */}
      {editingCV?.id === 'new' && (
        <div className="mb-3">
          <CVForm formData={formData} setFormData={setFormData} onSubmit={handleSubmit} onCancel={handleCancel} isNew />
        </div>
      )}

      {cvList.length === 0 ? (
        <p className="text-sm text-light-text-tertiary dark:text-dark-text-tertiary">No CVs specified</p>
      ) : (
        <div className="space-y-3">
          {cvList.map((cv) => (
            editingCV?.id === cv.id ? (
              <CVForm
                key={cv.id}
                formData={formData}
                setFormData={setFormData}
                onSubmit={handleSubmit}
                onCancel={handleCancel}
                isNew={false}
              />
            ) : (
              <div key={cv.id} className="border border-light-border-primary dark:border-dark-border-primary rounded-lg p-4 bg-light-bg-tertiary dark:bg-dark-bg-tertiary">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <CVTypeIcon type={cv.cv_type} className="w-4 h-4 text-light-text-tertiary dark:text-dark-text-tertiary shrink-0" />
                      <h4 className="font-medium text-sm text-light-text-primary dark:text-dark-text-primary">{cv.company_name}</h4>
                    </div>
                    {cv.specialisation && (
                      <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary mb-1">{cv.specialisation}</p>
                    )}
                    {cv.start_date && (
                      <p className="text-xs text-light-text-tertiary dark:text-dark-text-tertiary mb-2">
                        {formatCVDateRange(cv.start_date, cv.end_date, cv.cv_type)}
                      </p>
                    )}
                    <div className="flex flex-wrap gap-1.5">
                      {cv.cv_type && (
                        <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-md ${getCVTypeColor(cv.cv_type)}`}>
                          {getCVTypeLabel(cv.cv_type)}
                        </span>
                      )}
                      {canUpdate && (
                        <button
                          onClick={() => handleToggleVisibility(cv.id, cv.public_visible)}
                          className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-md transition-colors cursor-pointer hover:opacity-80 ${
                            cv.public_visible
                              ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                              : 'bg-light-bg-secondary dark:bg-dark-bg-secondary text-light-text-tertiary dark:text-dark-text-tertiary border border-light-border-primary dark:border-dark-border-primary'
                          }`}
                          title={cv.public_visible ? 'Click to hide from public' : 'Click to show publicly'}
                        >
                          {cv.public_visible ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                          {cv.public_visible ? 'Public' : 'Hidden'}
                        </button>
                      )}
                    </div>
                    {cv.description && (
                      <p className="mt-2 text-sm text-light-text-secondary dark:text-dark-text-secondary whitespace-pre-wrap break-words">{cv.description}</p>
                    )}
                    {cv.professions?.length > 0 && (
                      <div className="mt-2">
                        <p className="text-xs font-medium text-light-text-primary dark:text-dark-text-primary mb-1">Professions:</p>
                        <div className="flex flex-wrap gap-1.5">
                          {cv.professions.map((p, i) => (
                            <span key={i} className="inline-flex px-2 py-0.5 text-xs font-medium rounded-md bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
                              {getProfessionName(p)}{getDepartmentName(p) && ` · ${getDepartmentName(p)}`}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    {cv.industries?.length > 0 && (
                      <div className="mt-2">
                        <p className="text-xs font-medium text-light-text-primary dark:text-dark-text-primary mb-1">Industries:</p>
                        <div className="flex flex-wrap gap-1.5">
                          {cv.industries.map((ind, i) => (
                            <span key={i} className="inline-flex px-2 py-0.5 text-xs font-medium rounded-md bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400">
                              {typeof ind === 'object' ? ind.name || `Industry ${ind.industry_id || ind.id}` : `Industry ${ind}`}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                  {(canUpdate || canDelete) && (
                    <div className="flex items-center gap-1 shrink-0">
                      {canUpdate && (
                        <button
                          onClick={() => handleEdit(cv)}
                          className="p-1.5 text-light-text-tertiary dark:text-dark-text-tertiary hover:text-primary-600 dark:hover:text-primary-400 hover:bg-light-bg-secondary dark:hover:bg-dark-bg-secondary rounded transition-colors"
                          title="Edit"
                        >
                          <Edit className="w-3.5 h-3.5" />
                        </button>
                      )}
                      {canDelete && (
                        <button
                          onClick={() => handleDelete(cv)}
                          className="p-1.5 text-light-text-tertiary dark:text-dark-text-tertiary hover:text-red-600 dark:hover:text-red-400 hover:bg-light-bg-secondary dark:hover:bg-dark-bg-secondary rounded transition-colors"
                          title="Delete"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )
          ))}
        </div>
      )}
    </SectionBlock>

    <ConfirmDialog
      isOpen={!!deleteConfirm}
      onClose={() => setDeleteConfirm(null)}
      onConfirm={confirmDelete}
      confirmStyle="danger"
      title="Delete CV Entry"
      message={deleteConfirm ? `Are you sure you want to delete "${deleteConfirm.company_name}"? This action cannot be undone.` : ''}
      confirmText="Delete"
    />
    </>
  )
}

export default CVsSection
